import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES,
} from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { CreateAttributeValueRequest } from '@/modules/common/types/dto/request/custom-attribute-request';
import { ExtendDirection } from '@/modules/common/types/extend-direction';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';
import { ProjectTeamMember } from '@/modules/projects/types/project-team-member';
import { getDepartments } from '@/modules/settings/api/departments.api';
import { getRoles } from '@/modules/settings/api/roles.api';
import { getSeniorities } from '@/modules/settings/api/seniorities.api';
import { TEAM_DROPDOWN_PAGE_SIZE } from '@/modules/team/constants/pagination';
import { mapTeamMemberChangeTypeToDTO } from '@/modules/team/mappers/team-member-change-section.mapper';
import { mapTeamMemberChangesFromResponse } from '@/modules/team/mappers/team-member-changes.mapper';
import { mapTeamMemberConflictsFromDTO } from '@/modules/team/mappers/team-member-conflicts.mapper';
import { mapExtendDirectionToPeriod } from '@/modules/team/mappers/team-member-delete-change.mapper';
import { mapTeamMemberFullFromResponse } from '@/modules/team/mappers/team-member-full.mapper';
import { mapTeamMemberIntegrationsToPatchRequest } from '@/modules/team/mappers/team-member-integration.mapper';
import {
  mapTeamMemberFormDataToCreateRequest,
  mapTeamMemberFormDataToUpdateBillingRequest,
  mapTeamMemberFormDataToUpdateCapacityRequest,
  mapTeamMemberFormDataToUpdateInfoRequest,
} from '@/modules/team/mappers/team-member-request.mapper';
import { mapShortTeamMembersToDropdownItems } from '@/modules/team/mappers/team-member-short.mapper';
import { mapTeamMemberSummariesFromResponse } from '@/modules/team/mappers/team-member-summary.mapper';
import { mapTeamMembersWithRateFromResponse } from '@/modules/team/mappers/team-member-with-rate.mapper';
import { mockTeamMemberTags } from '@/modules/team/mock/team-member-tags.mock';
import { TeamMember, TeamMemberFormData, TeamMemberIntegration } from '@/modules/team/types';
import {
  FindShortTeamMembersFilterRequest,
  FindShortTeamMembersRequest,
} from '@/modules/team/types/dto/find-short-team-members.request';
import { ArchiveUnarchiveTeamMemberRequest } from '@/modules/team/types/dto/request/archive-unarchive-team-member-request';
import { DeleteTeamMemberChangeRequest } from '@/modules/team/types/dto/request/delete-team-member-change-request';
import { GetTeamMemberChangesRequest } from '@/modules/team/types/dto/request/get-team-member-changes-request';
import { InviteTeamMemberRequest } from '@/modules/team/types/dto/request/invite-team-member-request';
import { ReinviteTeamMemberRequest } from '@/modules/team/types/dto/request/reinvite-team-member-request';
import { RevokeTeamMemberAccessRequest } from '@/modules/team/types/dto/request/revoke-team-member-access-request';
import { AvailableArchivationDateResponse } from '@/modules/team/types/dto/response/available-archivation-date-response';
import { AvailableUnarchivationDateResponse } from '@/modules/team/types/dto/response/available-unarchivation-date-response';
import { TeamMemberChangeMetadataResponse } from '@/modules/team/types/dto/response/team-member-change-metadata-response';
import { TeamMemberFullResponse } from '@/modules/team/types/dto/response/team-member-full-response';
import { TeamMemberSummaryResponse } from '@/modules/team/types/dto/response/team-member-summary-response';
import { UpdateMemberResultResponse } from '@/modules/team/types/dto/response/update-member-result-response';
import {
  TeamMemberChangeSectionDTO,
  TeamMemberChangeSectionRequest,
} from '@/modules/team/types/dto/team-member-change-section.dto';
import { TeamMemberChangeConflictResponseDTO } from '@/modules/team/types/dto/team-member-conflict.dto';
import { ShortTeamMemberResponse } from '@/modules/team/types/dto/team-member-short.dto';
import { TeamMemberWithRateResponse } from '@/modules/team/types/dto/team-member-with-rate.dto';
import { PatchTeamMemberChangeParams } from '@/modules/team/types/patch-team-member-change-params';
import { TeamMemberListItem } from '@/modules/team/types/team-member';
import {
  TeamMemberChangeLogEntry,
  TeamMemberChangeType,
} from '@/modules/team/types/team-member-changes';
import { TeamMemberConflictResolutionData } from '@/modules/team/types/team-member-conflicts';
import { axiosInstance } from '@/utils/axios';
import { getTodayISOString, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

/**
 * Retrieves short team member list with pagination support
 * Used in dropdowns and selectors for team member selection
 * POST /api/team/members/short/search
 *
 * @param params - Filter and pagination parameters
 * @returns Paginated list of team members as DropdownSearchItem
 */
export const getShortTeamMembers = async (params?: {
  filter?: FindShortTeamMembersFilterRequest;
  pagination?: { cursor?: number | null; pageSize?: number };
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  const endpoint = '/api/team/members/short/search';

  const filter = params?.filter;
  const shouldAddDefaultRevisionDate = !filter?.matchesWholePeriod && !filter?.revisionDate;

  const requestBody: FindShortTeamMembersRequest = {
    filter: {
      ...filter,
      revisionDate: shouldAddDefaultRevisionDate ? getTodayISOString() : filter?.revisionDate,
    },
    pagination: {
      pageSize: params?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES,
      cursor: params?.pagination?.cursor,
    },
  };

  const response = await axiosInstance.post<{
    data: {
      items: ShortTeamMemberResponse[];
      pagination: {
        total: number;
        cursor: number | null;
      };
    };
    error?: { status: number; message: string; code: string };
  }>(endpoint, requestBody);

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  const apiData = response.data.data;

  return new PaginatedResponse({
    items: mapShortTeamMembersToDropdownItems(apiData.items),
    pagination: {
      total: apiData.pagination.total,
      cursor: apiData.pagination.cursor,
    },
  });
};

/**
 * Same as getShortTeamMembers but returns raw ShortTeamMemberResponse items
 * without mapping to DropdownSearchItem, preserving fields like
 * connectedIntegrations.
 */
export const getShortTeamMembersRaw = async (params?: {
  filter?: FindShortTeamMembersFilterRequest;
  pagination?: { cursor?: number | null; pageSize?: number };
}): Promise<PaginatedResponse<ShortTeamMemberResponse>> => {
  const endpoint = '/api/team/members/short/search';

  const filter = params?.filter;
  const shouldAddDefaultRevisionDate = !filter?.matchesWholePeriod && !filter?.revisionDate;

  const requestBody: FindShortTeamMembersRequest = {
    filter: {
      ...filter,
      revisionDate: shouldAddDefaultRevisionDate ? getTodayISOString() : filter?.revisionDate,
    },
    pagination: {
      pageSize: params?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES,
      cursor: params?.pagination?.cursor,
    },
  };

  const response = await axiosInstance.post<{
    data: {
      items: ShortTeamMemberResponse[];
      pagination: {
        total: number;
        cursor: number | null;
      };
    };
    error?: { status: number; message: string; code: string };
  }>(endpoint, requestBody);

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  const apiData = response.data.data;

  return new PaginatedResponse({
    items: apiData.items,
    pagination: {
      total: apiData.pagination.total,
      cursor: apiData.pagination.cursor,
    },
  });
};

/**
 * Loads team members with rate information for dropdown with cursor-based pagination support.
 * Returns full ProjectTeamMember data including rate information.
 * Used in project team member management and user selection.
 *
 * @param searchString - Optional search term to filter team members by name
 * @param cursor - Pagination cursor for loading next page
 * @param currencyCode - Optional currency code for rate conversion (e.g., USD, EUR)
 * @param userIds - Optional array of user IDs to filter specific team members
 */
export const getTeamMembersWithRates = async (
  searchString?: string,
  cursor?: number | null,
  currencyCode?: string,
  userIds?: number[]
): Promise<PaginatedResponse<ProjectTeamMember>> => {
  const endpoint = '/api/team/members-with-rates';

  const params: Record<string, any> = {
    pageSize: TEAM_DROPDOWN_PAGE_SIZE,
  };

  if (cursor !== null && cursor !== undefined) {
    params.cursor = cursor;
  }

  if (searchString) {
    params.searchString = searchString;
  }

  if (currencyCode) {
    params.currencyCode = currencyCode;
  }

  if (userIds && userIds.length > 0) {
    params.userIds = userIds.join(',');
  }

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBERS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: {
        items: TeamMemberWithRateResponse[];
        pagination: {
          total: number;
          cursor?: number | null;
        };
      };
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params,
    })
  );

  return new PaginatedResponse({
    items: mapTeamMembersWithRateFromResponse(apiData.items),
    pagination: {
      ...apiData.pagination,
      cursor: apiData.pagination.cursor ?? null,
    },
  });
};

/**
 * Retrieves paginated list of team members with filters and search.
 * POST /api/team/members/search
 *
 * @param params - Cursor, filters for the team members search
 * @returns Paginated list of team member list items
 */
export const getTeamMembers = async (params: {
  cursor?: number;
  filters?: Record<string, any>;
}): Promise<PaginatedResponse<TeamMemberListItem>> => {
  const endpoint = '/api/team/members/search';

  const requestBody = {
    ...(params.filters || {}),
    pagination: {
      pageSize: DEFAULT_PAGE_SIZE,
      cursor: params.cursor,
    },
  };

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBERS_LOAD_FAILED, () =>
    axiosInstance.post<{
      data: PaginatedResponse<TeamMemberSummaryResponse>;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );

  return new PaginatedResponse({
    items: mapTeamMemberSummariesFromResponse(apiData.items || []),
    pagination: apiData.pagination || { total: 0, cursor: null },
  });
};

/**
 * Retrieves detailed team member information by ID.
 * Returns full team member data including info, capacity, billing, and integrations.
 */
export const getTeamMemberDetails = async (memberId: string): Promise<TeamMember> => {
  const endpoint = '/api/team/members/one/full';

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_NOT_FOUND, () =>
    axiosInstance.get<{
      data: TeamMemberFullResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: { id: parseInt(memberId, 10) },
    })
  );

  return mapTeamMemberFullFromResponse(apiData);
};

/**
 * Creates a new team member with the provided configuration.
 * Accepts TeamMemberFormData and transforms it to the API format.
 * Returns the created team member ID for subsequent operations (e.g., avatar upload).
 */
export const createTeamMember = async (data: TeamMemberFormData): Promise<number> => {
  const endpoint = '/api/team/members/one';
  const apiRequest = mapTeamMemberFormDataToCreateRequest(data);

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_CREATE_FAILED, () =>
    axiosInstance.post<{
      data: { id: number };
      error?: { status: number; message: string; code: string };
    }>(endpoint, apiRequest)
  );

  return response.id;
};

/**
 * Updates team member info section.
 * Accepts partial TeamMemberFormData and transforms it to the API format.
 */
export const updateTeamMemberInfo = async (
  memberId: number,
  data: Partial<TeamMemberFormData>,
  effectiveStartDate: Date,
  effectiveEndDate?: Date
): Promise<PatchEntityResult> => {
  const endpoint = '/api/team/members/info';
  const patchRequest = mapTeamMemberFormDataToUpdateInfoRequest(
    data,
    memberId,
    effectiveStartDate,
    effectiveEndDate
  );

  return await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: UpdateMemberResultResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

/**
 * Updates only custom attribute values for a team member.
 * Sends existing info fields (to satisfy BE required fields) + new customAttributesValues.
 */
export const updateTeamMemberCustomAttributes = async (
  memberId: number,
  values: CreateAttributeValueRequest[]
): Promise<void> => {
  const endpoint = `/api/team/members/${memberId}/custom-attributes`;

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_UPDATE_FAILED, () =>
    axiosInstance.put(endpoint, { values })
  );
};

/**
 * Updates team member billing section.
 * Accepts partial TeamMemberFormData and transforms it to the API format.
 */
export const updateTeamMemberBilling = async (
  memberId: number,
  data: Partial<TeamMemberFormData>,
  effectiveStartDate: Date,
  effectiveEndDate?: Date
): Promise<PatchEntityResult> => {
  const endpoint = '/api/team/members/billing';
  const patchRequest = mapTeamMemberFormDataToUpdateBillingRequest(
    data,
    memberId,
    effectiveStartDate,
    effectiveEndDate
  );

  return await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: UpdateMemberResultResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

/**
 * Updates team member capacity section.
 * Accepts partial TeamMemberFormData and transforms it to the API format.
 */
export const updateTeamMemberCapacity = async (
  memberId: number,
  data: Partial<TeamMemberFormData>,
  effectiveStartDate: Date,
  effectiveEndDate?: Date
): Promise<PatchEntityResult> => {
  const endpoint = '/api/team/members/capacity';
  const patchRequest = mapTeamMemberFormDataToUpdateCapacityRequest(
    data,
    memberId,
    effectiveStartDate,
    effectiveEndDate
  );

  return await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: UpdateMemberResultResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

/**
 * Archives a team member with effective date.
 * Archived members are hidden from active views but data is preserved.
 * PATCH /api/team/member-changes/archive
 */
export const archiveTeamMember = async ({
  memberId,
  archiveDate,
}: {
  memberId: string;
  archiveDate: Date;
}): Promise<void> => {
  const endpoint = '/api/team/member-changes/archive';

  const requestBody: ArchiveUnarchiveTeamMemberRequest = {
    teamMemberId: parseInt(memberId, 10),
    effectiveDate: normalizeToStartOfDayUTC(archiveDate).toISOString(),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_ARCHIVE_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );
};

/**
 * Restores an archived team member to active status with effective date.
 * Makes the member visible and trackable again.
 * PATCH /api/team/member-changes/unarchive
 */
export const unarchiveTeamMember = async ({
  memberId,
  unarchiveDate,
}: {
  memberId: string;
  unarchiveDate: Date;
}): Promise<void> => {
  const endpoint = '/api/team/member-changes/unarchive';

  const requestBody: ArchiveUnarchiveTeamMemberRequest = {
    teamMemberId: parseInt(memberId, 10),
    effectiveDate: normalizeToStartOfDayUTC(unarchiveDate).toISOString(),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_UNARCHIVE_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );
};

/**
 * Permanently deletes multiple team members and all associated data.
 * Warning: This action cannot be undone. Consider archiving instead.
 * DELETE /api/opsium-users/bulk
 */
export const deleteTeamMembers = async (ids: number[]): Promise<void> => {
  const endpoint = '/api/opsium-users/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_DELETE_FAILED, () =>
    axiosInstance.delete<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      data: { ids },
    })
  );
};

/**
 * Retrieves the earliest available date for archiving a team member.
 * Team members cannot be archived before this date.
 * GET /api/team/member-changes/archive/available-date
 */
export const getAvailableArchivationDate = async (memberId: string): Promise<Date> => {
  const endpoint = '/api/team/member-changes/archive/available-date';
  const teamMemberId = parseInt(memberId, 10);

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.TEAM_MEMBER_AVAILABLE_ARCHIVE_DATE_FAILED,
    () =>
      axiosInstance.get<{
        data: AvailableArchivationDateResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: { teamMemberId },
      })
  );

  return new Date(response.availableArchivationDate);
};

/**
 * Retrieves the earliest available date for unarchiving a team member.
 * Returns null if there are no restrictions on unarchivation.
 * GET /api/team/member-changes/unarchive/available-date
 */
export const getAvailableUnarchivationDate = async (memberId: string): Promise<Date | null> => {
  const endpoint = '/api/team/member-changes/unarchive/available-date';
  const teamMemberId = parseInt(memberId, 10);

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.TEAM_MEMBER_AVAILABLE_UNARCHIVE_DATE_FAILED,
    () =>
      axiosInstance.get<{
        data: AvailableUnarchivationDateResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: { teamMemberId },
      })
  );

  return response.availableUnarchivationDate ? new Date(response.availableUnarchivationDate) : null;
};

/**
 * Uploads team member avatar image file using presigned URL approach.
 * Steps:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to S3 using presigned URL
 * 3. Return the final accessible URL
 */
export const uploadTeamMemberAvatar = async ({
  userId,
  file,
}: {
  userId: number;
  file: File;
}): Promise<string> => {
  const endpoint = '/api/team/members/one/avatar-presigned-url';

  // Step 1: Get presigned URL from backend
  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.TEAM_MEMBER_AVATAR_UPLOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: { presignedUrl: string };
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: {
          userId,
          fileName: file.name,
          contentLength: file.size,
        },
      })
  );

  const { presignedUrl } = response;

  // Step 2: Upload file to S3 using presigned URL
  await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  // Step 3: Extract the base URL (without query parameters) as the accessible avatar URL
  return presignedUrl.split('?')[0];
};

export const deleteTeamMemberAvatar = async (userId: number): Promise<void> => {
  const endpoint = '/api/team/members/one/avatar';

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_AVATAR_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint, { data: { userId } })
  );
};

/**
 * Sends an invitation email to a team member with basic status.
 * Changes member status to 'invited' and sends access credentials.
 * POST /api/team/invite
 */
export const inviteTeamMember = async (
  memberId: string,
  groupId?: number,
  email?: string
): Promise<void> => {
  const endpoint = '/api/team/invite';

  const requestBody: InviteTeamMemberRequest = {
    teamMemberId: parseInt(memberId, 10),
    groupId,
    ...(email ? { email } : {}),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_INVITE_FAILED, () =>
    axiosInstance.post<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );
};

/**
 * Resends invitation email to a team member with expired status.
 * Generates new invitation link and updates expiry date.
 * POST /api/team/reinvite
 */
export const resendInviteTeamMember = async (memberId: string): Promise<void> => {
  const endpoint = '/api/team/reinvite';

  const requestBody: ReinviteTeamMemberRequest = {
    teamMemberId: parseInt(memberId, 10),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_REINVITE_FAILED, () =>
    axiosInstance.post<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );
};

/**
 * Revokes platform access for a team member.
 * Changes status to 'basic', removing login access while preserving data.
 * PATCH /api/team/revoke
 */
export const revokeTeamMemberAccess = async (memberId: string): Promise<void> => {
  const endpoint = '/api/team/revoke';

  const requestBody: RevokeTeamMemberAccessRequest = {
    teamMemberId: parseInt(memberId, 10),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_REVOKE_ACCESS_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );
};

/**
 * Retrieves available tags for team member classification.
 */
export const getTags = async (): Promise<string[]> => {
  try {
    const response = await axiosInstance.get('/api/team-members/tags');

    if (response.data?.error) {
      // Return mock data instead of throwing
      return new Promise(resolve => {
        setTimeout(() => resolve(mockTeamMemberTags || []), 2000);
      });
    }

    return Promise.resolve(response.data?.data || mockTeamMemberTags || []);
  } catch (error) {
    // Mock implementation for development
    return new Promise(resolve => {
      setTimeout(() => resolve(mockTeamMemberTags || []), 2000);
    });
  }
};

/**
 * Updates team member integrations.
 * Only includes integrations with selected values - omitted integrations are unlinked.
 */
export const updateTeamMemberIntegrations = async (
  teamMemberId: string | number,
  integrations?: TeamMemberIntegration[]
): Promise<void> => {
  const endpoint = '/api/team/member-changes/integrations';
  const patchRequest = mapTeamMemberIntegrationsToPatchRequest(teamMemberId, integrations);

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

/**
 * Retrieves team member change history with pagination and filtering
 */
export const getTeamMemberChanges = async (
  request: GetTeamMemberChangesRequest
): Promise<PaginatedResponse<TeamMemberChangeLogEntry>> => {
  const endpoint = '/api/team/member-changes/search';

  const apiData = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.TEAM_MEMBER_CHANGES_LOAD_FAILED,
    () =>
      axiosInstance.post<{
        data: {
          items: TeamMemberChangeMetadataResponse[];
          pagination: { total: number; cursor: number | null };
        };
        error?: { status: number; message: string; code: string };
      }>(endpoint, request)
  );

  return new PaginatedResponse({
    items: mapTeamMemberChangesFromResponse(apiData.items),
    pagination: apiData.pagination,
  });
};

/**
 * Deletes a team member change and extends/shifts adjacent change
 */
export const deleteTeamMemberChange = async ({
  teamMemberId,
  section,
  stateDeltaId,
  extendDirection,
}: {
  teamMemberId: number;
  section: TeamMemberChangeType;
  stateDeltaId: number;
  extendDirection: ExtendDirection;
}): Promise<void> => {
  const endpoint = '/api/team/member-changes';

  const requestBody: DeleteTeamMemberChangeRequest = {
    teamMemberId,
    stateDeltaId,
    section: mapTeamMemberChangeTypeToDTO(section),
    period: mapExtendDirectionToPeriod(extendDirection),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_CHANGE_DELETE_FAILED, () =>
    axiosInstance.delete<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      data: requestBody,
    })
  );
};

/**
 * Base function for checking team member conflicts for a specific section
 * GET /api/team/member-changes/conflicts
 */
const getTeamMemberConflicts = async ({
  teamMemberId,
  section,
  startDate,
  endDate,
}: {
  teamMemberId: number;
  section: TeamMemberChangeSectionRequest;
  startDate: string;
  endDate?: string | null;
}): Promise<TeamMemberConflictResolutionData> => {
  const endpoint = '/api/team/member-changes/conflicts';

  const params: Record<string, any> = {
    teamMemberId,
    section,
    startDate,
  };

  if (endDate) {
    params.endDate = endDate;
  }

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.TEAM_MEMBER_INFO_CONFLICTS_LOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: TeamMemberChangeConflictResponseDTO[];
        error?: { status: number; message: string; code: string };
      }>(endpoint, { params })
  );

  return mapTeamMemberConflictsFromDTO(response);
};

/**
 * Checks for conflicts in team member INFO section changes for a specific date range
 * Returns list of existing snapshots that will be affected by the change
 *
 * @param teamMemberId - ID of the team member
 * @param startDate - Start date of the new change (ISO format)
 * @param endDate - Optional end date of the new change (ISO format)
 * @returns Conflict resolution data with affected snapshots
 */
export const getTeamMemberInfoConflicts = async ({
  teamMemberId,
  startDate,
  endDate,
}: {
  teamMemberId: number;
  startDate: string;
  endDate?: string | null;
}): Promise<TeamMemberConflictResolutionData> => {
  return getTeamMemberConflicts({
    teamMemberId,
    section: TeamMemberChangeSectionDTO.INFO,
    startDate,
    endDate,
  });
};

/**
 * Checks for conflicts in team member BILLING section changes for a specific date range
 * Returns list of existing snapshots that will be affected by the change
 *
 * @param teamMemberId - ID of the team member
 * @param startDate - Start date of the new change (ISO format)
 * @param endDate - Optional end date of the new change (ISO format)
 * @returns Conflict resolution data with affected snapshots
 */
export const getTeamMemberBillingConflicts = async ({
  teamMemberId,
  startDate,
  endDate,
}: {
  teamMemberId: number;
  startDate: string;
  endDate?: string | null;
}): Promise<TeamMemberConflictResolutionData> => {
  return getTeamMemberConflicts({
    teamMemberId,
    section: TeamMemberChangeSectionDTO.BILLING,
    startDate,
    endDate,
  });
};

/**
 * Checks for conflicts in team member CAPACITY section changes for a specific date range
 * Returns list of existing snapshots that will be affected by the change
 *
 * @param teamMemberId - ID of the team member
 * @param startDate - Start date of the new change (ISO format)
 * @param endDate - Optional end date of the new change (ISO format)
 * @returns Conflict resolution data with affected snapshots
 */
export const getTeamMemberCapacityConflicts = async ({
  teamMemberId,
  startDate,
  endDate,
}: {
  teamMemberId: number;
  startDate: string;
  endDate?: string | null;
}): Promise<TeamMemberConflictResolutionData> => {
  return getTeamMemberConflicts({
    teamMemberId,
    section: TeamMemberChangeSectionDTO.CAPACITY,
    startDate,
    endDate,
  });
};

/**
 * Fetches full team member state at a specific revision date.
 * Used in EditChange mode to load historical team member data.
 * Uses the same endpoint as getTeamMemberDetails but with a revisionDate parameter.
 * Returns full TeamMember data (info + billing + capacity) at that point in time.
 *
 * GET /api/team/members/one/full?id={id}&revisionDate={date}
 */
export const getTeamMemberDetailsByRevisionDate = async (
  memberId: number,
  revisionDate: string
): Promise<TeamMember> => {
  const endpoint = '/api/team/members/one/full';

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_NOT_FOUND, () =>
    axiosInstance.get<{
      data: TeamMemberFullResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: { id: memberId, revisionDate },
    })
  );

  return mapTeamMemberFullFromResponse(apiData);
};

/**
 * Updates a specific team member change.
 * Used in EditChange mode to update an existing change record.
 * Uses section-specific endpoints with stateDeltaId to identify the change.
 *
 * Endpoints:
 * - PATCH /api/team/member-changes/info (with stateDeltaId)
 * - PATCH /api/team/member-changes/billing (with stateDeltaId)
 * - PATCH /api/team/member-changes/capacity (with stateDeltaId)
 * - PATCH /api/team/member-changes/integrations (with stateDeltaId)
 */
export const patchTeamMemberChange = async (params: PatchTeamMemberChangeParams): Promise<void> => {
  const { stateDeltaId, teamMemberId, data, effectiveStartDate, effectiveEndDate, changeType } =
    params;

  let endpoint: string;
  let patchRequest: Record<string, any>;

  switch (changeType) {
    case TeamMemberChangeType.Info:
      endpoint = '/api/team/member-changes/info';
      patchRequest = mapTeamMemberFormDataToUpdateInfoRequest(
        data,
        Number(teamMemberId),
        effectiveStartDate,
        effectiveEndDate ?? undefined
      );
      break;
    case TeamMemberChangeType.Billing:
      endpoint = '/api/team/member-changes/billing';
      patchRequest = mapTeamMemberFormDataToUpdateBillingRequest(
        data,
        Number(teamMemberId),
        effectiveStartDate,
        effectiveEndDate ?? undefined
      );
      break;
    case TeamMemberChangeType.Capacity:
      endpoint = '/api/team/member-changes/capacity';
      patchRequest = mapTeamMemberFormDataToUpdateCapacityRequest(
        data,
        Number(teamMemberId),
        effectiveStartDate,
        effectiveEndDate ?? undefined
      );
      break;
    case TeamMemberChangeType.Integration:
      endpoint = '/api/team/member-changes/integrations';
      patchRequest = mapTeamMemberIntegrationsToPatchRequest(
        Number(teamMemberId),
        data.integrations
      );
      break;
    default:
      return;
  }

  // Add the stateDeltaId to identify the existing change record
  patchRequest.stateDeltaId = stateDeltaId;

  await withApiErrorHandler(endpoint, ApiErrorCode.TEAM_MEMBER_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

// Re-export functions from settings API for convenience
export { getDepartments, getRoles, getSeniorities };
